import {
    Box,
    Typography,
    useTheme,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    LinearProgress
} from '@mui/material';
import React, { useEffect } from 'react';
import PortalCard from '../../../../../components/PortalCard';
import analyticsService from '../../../../../services/analytics/AnalyticsService';

const CategoryStockHealth: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState<Array<{
        category: string;
        totalStock: number;
        lowStockCount: number;
        mediumStockCount: number;
        highStockCount: number;
        stockHealthPercentage: number;
    }>>([]);
    const [error, setError] = React.useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await analyticsService.getCategoryStockHealth();
                if (result.success && result.data) {
                    setData(result.data);
                } else {
                    setError(result.message || 'Failed to fetch category stock health');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to fetch category stock health');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const getHealthColor = (percentage: number) => {
        if (percentage >= 70) return theme.palette.success.main;
        if (percentage >= 40) return theme.palette.warning.main;
        return theme.palette.error.main;
    };

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    Category Stock Health
                </Typography>
                <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                    Stock health percentage by category
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, width: '100%' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='error' fontFamily='primaryFont'>
                            {error}
                        </Typography>
                    </Box>
                ) : data.length > 0 ? (
                    <TableContainer component={Paper} variant='outlined'>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell fontFamily='primaryFont' fontWeight='bold'>Category</TableCell>
                                    <TableCell align='right' fontFamily='primaryFont' fontWeight='bold'>Total Stock</TableCell>
                                    <TableCell align='right' fontFamily='primaryFont' fontWeight='bold'>Low</TableCell>
                                    <TableCell align='right' fontFamily='primaryFont' fontWeight='bold'>Medium</TableCell>
                                    <TableCell align='right' fontFamily='primaryFont' fontWeight='bold'>High</TableCell>
                                    <TableCell align='right' fontFamily='primaryFont' fontWeight='bold'>Health %</TableCell>
                                    <TableCell fontFamily='primaryFont' fontWeight='bold'>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((row) => (
                                    <TableRow key={row.category}>
                                        <TableCell fontFamily='primaryFont'>{row.category}</TableCell>
                                        <TableCell align='right' fontFamily='primaryFont'>{row.totalStock.toLocaleString()}</TableCell>
                                        <TableCell align='right' fontFamily='primaryFont'>{row.lowStockCount}</TableCell>
                                        <TableCell align='right' fontFamily='primaryFont'>{row.mediumStockCount}</TableCell>
                                        <TableCell align='right' fontFamily='primaryFont'>{row.highStockCount}</TableCell>
                                        <TableCell align='right' fontFamily='primaryFont'>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LinearProgress
                                                    variant='determinate'
                                                    value={row.stockHealthPercentage}
                                                    sx={{
                                                        flexGrow: 1,
                                                        height: 8,
                                                        borderRadius: 4,
                                                        backgroundColor: `${getHealthColor(row.stockHealthPercentage)}30`,
                                                        '& .MuiLinearProgress-bar': {
                                                            backgroundColor: getHealthColor(row.stockHealthPercentage),
                                                        }
                                                    }}
                                                />
                                                <Typography variant='body2' fontFamily='primaryFont' fontWeight={600} sx={{ minWidth: 45 }}>
                                                    {row.stockHealthPercentage.toFixed(1)}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant='caption'
                                                fontFamily='primaryFont'
                                                sx={{
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    backgroundColor: `${getHealthColor(row.stockHealthPercentage)}20`,
                                                    color: getHealthColor(row.stockHealthPercentage),
                                                    fontWeight: 600
                                                }}
                                            >
                                                {row.stockHealthPercentage >= 70 ? 'Healthy' : row.stockHealthPercentage >= 40 ? 'Warning' : 'Critical'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                            No data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default CategoryStockHealth;

